import { MenuOutlined } from "@ant-design/icons";
import { Button, Dropdown, MenuProps } from "antd";
import { ItemType } from "antd/es/menu/interface";
import { capitalize } from "lodash-es";
import type { Icon, Layout, ModeBarButton, ModeBarButtonAny } from "plotly.js";
import { FC, useRef } from "react";
import { FaHome } from "react-icons/fa";
import { ThemeUtil } from "src/util/ThemeUtil";

const TOGGLED_BG = '#def';

/** Highlights a toggle item of the graph menu while it is on. */
export function setToggled(style: React.CSSProperties, on: boolean) {
  style.backgroundColor = on ? ThemeUtil.getBgColor(TOGGLED_BG) : undefined;
}

// Inlined from plotly.js Icons.tooltip_compare — hardcoded so this module does
// not statically pull the full plotly.js bundle into the eager chunk.
const compareIcon = <svg width="1em" height="1em" fill="currentColor" viewBox="0 0 1125 1000"><path d="m187 786l0 2-187-188 188-187 0 0 937 0 0 373-938 0z m0-499l0 1-187-188 188-188 0 0 937 0 0 376-938-1z" transform="matrix(1 0 0 -1 0 850)"></path></svg>;

const plotlyIcon = (icon: Icon) => <svg width="1em" height="1em" fill="currentColor" viewBox={`0 0 ${icon.width} ${icon.height}`}>
  <path d={icon.path} transform={icon.transform} />
</svg>;

const isCustomModebarBtn = (btn: ModeBarButtonAny): btn is ModeBarButton => typeof btn === 'object';

interface GraphBtnsProps {
  dropdownItems: MenuProps;
  layout: Partial<Layout>;
  refresh: () => void;
  modebarBtns: ModeBarButtonAny[][];
  /** One hamburger menu holding every button, Plotly's modebar included (hidden by .xm-graph-menu-collapsed). */
  collapsed?: boolean;
  /** Colours the reset item like Plotly's modebar button while a zoom is on. */
  zoomed?: boolean;
}

export const GraphBtns: FC<GraphBtnsProps> = ({ dropdownItems, layout, refresh, modebarBtns, collapsed, zoomed }) => {
  const ref = useRef<HTMLDivElement>(null);
  const compareActive = layout.hovermode !== 'closest';

  const toggleCompare = () => {
    layout.hovermode = compareActive ? 'closest' : 'x';
    refresh();
  };

  if (!collapsed) {
    return <div className={`xm-plot-btns offset-right-${modebarBtns.flat().length}`}>
      {dropdownItems?.items?.length ? <Dropdown menu={dropdownItems} placement="bottomRight">
        <Button size="small" type="text">...</Button>
      </Dropdown>
        : null}
      <Button type="link" size="small" icon={compareIcon} title="Compare data on hover"
        className={compareActive ? null : 'inactive'} onClick={toggleCompare} />
    </div>;
  }

  // Plotly's own handlers keep their semantics (initial ranges, favorite): the menu clicks the hidden modebar button.
  const clickModebarBtn = (selector: string) =>
    ref.current?.closest('.xm-graph')?.querySelector<HTMLElement>(`.modebar-btn${selector}`)?.click();

  const ownItems = dropdownItems?.items ?? [];
  const items: ItemType[] = [
    ...ownItems,
    ...(ownItems.length ? [{ type: 'divider' } as const] : []),
    {
      key: 'compare', icon: compareIcon, label: 'Compare', title: 'Compare data on hover',
      style: { backgroundColor: compareActive ? ThemeUtil.getBgColor(TOGGLED_BG) : undefined }, onClick: toggleCompare,
    },
    {
      key: 'resetAxes', icon: <FaHome style={zoomed ? { color: 'var(--ant-color-primary-hover)' } : undefined} />, label: 'Reset', title: 'Reset axes',
      onClick: () => clickModebarBtn('[data-attr="zoom"][data-val="reset"]'),
    },
    ...modebarBtns.flat().filter(isCustomModebarBtn).map(btn => ({
      key: btn.name,
      icon: typeof btn.icon === 'object' ? plotlyIcon(btn.icon) : null,
      label: capitalize(btn.name),
      title: btn.title,
      onClick: () => clickModebarBtn(`[data-attr="${btn.attr}"]`),
    })),
  ];

  return <div ref={ref} className="xm-plot-btns offset-right-0">
    <Dropdown menu={{ items }} trigger={['click']} placement="bottomRight">
      <Button size="small" type="text" icon={<MenuOutlined />} title="Graph menu" />
    </Dropdown>
  </div>;
};
